import React from 'react';
import P from 'prop-types';
// eslint-disable-next-line lodash/import-scope
import property from 'lodash/property';
// eslint-disable-next-line lodash/import-scope
import get from 'lodash/get';
// eslint-disable-next-line lodash/import-scope
import set from 'lodash/set';
import { connect } from 'react-redux';
import { spacing } from '@nerdwallet/base-styles/variables';
import { createStructuredSelector } from 'reselect';
import classes from '@nerdwallet/base-styles/classes';
import classNames from 'classnames';
import BackgroundColor from '../background-color/background-color';
import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  stringInputSpec,
  stringSelectSpec,
  wrapper,
} from '../../lib/editing-configs';
import styles from './universal-container.module.less';

export const universalContainerPropTypes = {
  children: P.node,
  backgroundImageDesktop: P.string,
  backgroundImageTablet: P.string,
  backgroundImageMobile: P.string,
  backgroundSize: P.oneOf(['contain', 'cover', 'auto']),
  size: P.string,
  backgroundColor: P.string,
  hexColor: P.string,
  padding: P.shape({
    type: P.string,
    currency: {
      top: P.string,
      right: P.string,
      bottom: P.string,
      left: P.string,
    },
    custom: {
      top: P.string,
      right: P.string,
      bottom: P.string,
      left: P.string,
    },
  }),
  margin: P.shape({
    type: P.string,
    currency: {
      top: P.string,
      right: P.string,
      bottom: P.string,
      left: P.string,
    },
    custom: {
      top: P.string,
      right: P.string,
      bottom: P.string,
      left: P.string,
    },
  }),
  border: P.shape({
    type: P.string,
    borderWidth: P.string,
    borderColor: P.string,
    shadowColor: P.string,
    borderRounded: P.shape({
      type: P.string,
      value: P.oneOfType([P.string, P.number]),
      currencyValue: P.string,
    }),
  }),
  width: P.shape({
    type: P.string,
    screenSizes: P.shape({
      lg: P.bool,
      md: P.bool,
      sm: P.bool,
    }),
    currency: {
      value: P.string,
    },
    custom: {
      value: P.string,
    },
  }),
  alignment: P.shape({
    vertical: P.string,
    horizontal: P.string,
    direction: P.string,
    type: P.string,
  }),
  hide: P.shape({
    lg: P.bool,
    md: P.bool,
    sm: P.bool,
  }),
  style: P.object,
};

export const universalContainerDefaultProps = {
  children: null,
  backgroundImageDesktop: '',
  backgroundImageTablet: '',
  backgroundImageMobile: '',
  backgroundSize: 'cover',
  size: 'lg',
  padding: {
    type: 'currency',
    currency: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    custom: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  },
  margin: {
    type: 'currency',
    currency: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    custom: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  },
  border: {
    type: 'none',
    borderWidth: 1,
    borderColor: '#cdcdcd',
    shadowColor: '',
    borderRounded: {
      type: 'currency',
      value: '0',
      currencyValue: 'border-radius-1',
    },
  },
  width: {
    type: 'currency',
    screenSizes: {
      lg: false,
      md: false,
      sm: false,
    },
    currency: {
      value: '',
    },
    custom: {
      value: '',
    },
  },
  alignment: {
    type: 'none',
    vertical: 'flex-start',
    horizontal: 'flex-start',
    direction: 'column',
  },
  hide: {
    lg: false,
    md: false,
    sm: false,
  },
};

const backgroundSizes = [
  { label: 'Cover', value: 'cover' },
  { label: 'Contain', value: 'contain' },
  { label: 'Auto', value: 'auto' },
];

const moveArrayItemPosition = (array, oldIndex, newIndex) => {
  let resultIndex = newIndex;
  if (newIndex >= array.length) {
    resultIndex = array.length - 1;
  }
  array.splice(resultIndex, 0, array.splice(oldIndex, 1)[0]);
  return array;
};

// Parsing base-styles spacing values to array and removing "--wide" entries
const mapCurrencySpacingSizes = Array.from(
  new Map(Object.entries(spacing)),
  ([key, value]) => ({ label: key, value })
).filter((item) => !item.label.includes('--wide'));

// Let's use "moveArrayItemPosition" to prevent the "3-half" size from being in the last position
const currencySpacingSizes = moveArrayItemPosition(
  mapCurrencySpacingSizes,
  6,
  4
);

export const capitalize = (text) =>
  text.toLowerCase().charAt(0).toUpperCase() + text.slice(1);

// Once Top, Left, Right and bottom will be the same for both padding and margin
// let's avoid repeatable code and use this builder to handle this.
const buildSpacingInputs = (
  inputComponent,
  fielName,
  fieldList = [],
  extraLabel = '',
  isSelectInput
) => {
  const help = {
    help: 'Defaults to "0"',
  };
  if (isSelectInput) {
    return fieldList.map((input) =>
      inputComponent(
        `${fielName}.${input}`,
        `${capitalize(input)}`,
        currencySpacingSizes,
        help
      )
    );
  }
  return fieldList.map((input) =>
    inputComponent(
      `${fielName}.${input}`,
      `${capitalize(input)} ${extraLabel}`,
      help
    )
  );
};

const DefaultContent = () => <h1>Default Content</h1>;
/**
 * TODO: implement all params doc/types
 * @param {{style: React.CSSProperties}}
 * @returns
 */
const UniversalContainer = ({
  children,
  backgroundImageDesktop,
  backgroundImageTablet,
  backgroundImageMobile,
  size,
  backgroundColor: bgColor,
  hexColor,
  backgroundSize,
  padding,
  margin,
  border,
  width,
  alignment,
  hide,
  style,
  ...rest
}) => {
  const backgroundImageSrcMap = {
    lg: backgroundImageDesktop,
    md: backgroundImageTablet,
    sm: backgroundImageMobile,
  };

  const {
    borderColor,
    borderWidth,
    shadowColor,
    type: borderType,
    borderRounded,
  } = border;

  const borderColorStyle =
    borderType === 'color'
      ? {
          border: `${borderWidth || 0}px solid ${borderColor || '#cdcdcd'}`,
        }
      : null;

  const borderShadowStyle =
    borderType === 'shadow' && shadowColor
      ? {
          boxShadow: `0px 1px 10px -1px ${shadowColor}`,
        }
      : null;

  const borderRadiusStyle =
    borderType === 'rounded' &&
    borderRounded?.type === 'custom' &&
    borderRounded?.value
      ? {
          borderRadius: borderRounded?.value,
        }
      : null;

  const backgroundImage = backgroundImageSrcMap[size]
    ? `url(${backgroundImageSrcMap[size]})`
    : null;

  const backgroundSizeStyle = backgroundImageSrcMap[size]
    ? `${backgroundSize}`
    : null;

  const bgOptions = { backgroundColor: bgColor, hexColor };

  const applyCurrencyRoundedBorders =
    borderType === 'rounded' &&
    (borderRounded?.type === 'currency' || 'currency') &&
    (borderRounded?.currencyValue || 'border-radius-1');

  const paddingType = padding?.type || 'currency';
  const marginType = margin?.type || 'currency';

  const widthType = width?.type || 'currency';

  const getWidth = () => {
    const sizes = Object.entries(width?.screenSizes || {})?.reduce(
      (acc, [key, value]) => (value ? [...acc, key] : acc),
      []
    );

    if (!sizes.length && widthType) {
      return {
        width: width?.[widthType]?.value,
      };
    }

    return {
      ...(sizes?.length &&
        sizes?.includes(size) && {
          width: width?.[widthType]?.value,
        }),
    };
  };

  const getHideSize = () => {
    const sizes = Object.entries(hide || {})?.reduce(
      (acc, [key, value]) => (value ? [...acc, key] : acc),
      []
    );
    return {
      ...(sizes.length && sizes.includes(size) && { display: 'none' }),
    };
  };

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <div
          {...rest}
          className={classNames('container', [
            styles.container,
            {
              [classes(borderRounded?.currencyValue)]:
                applyCurrencyRoundedBorders,
              [classes('display-flex')]: alignment?.type === 'flex',
            },
          ])}
          style={{
            ...getHideSize(),
            ...getWidth(),
            ...(alignment?.direction &&
              alignment?.type === 'flex' && {
                flexDirection: alignment?.direction,
              }),
            ...(alignment?.horizontal &&
              alignment?.type === 'flex' && {
                alignItems: alignment?.horizontal,
              }),
            ...(alignment?.vertical &&
              alignment?.type === 'flex' && {
                justifyContent: alignment?.vertical,
              }),
            backgroundColor,
            backgroundImage,
            paddingTop: padding?.[paddingType]?.top,
            paddingRight: padding?.[paddingType]?.right,
            paddingBottom: padding?.[paddingType]?.bottom,
            paddingLeft: padding?.[paddingType]?.left,
            marginTop: margin?.[marginType]?.top,
            marginRight: margin?.[marginType]?.right,
            marginBottom: margin?.[marginType]?.bottom,
            marginLeft: margin?.[marginType]?.left,
            backgroundSize: backgroundSizeStyle,
            ...borderColorStyle,
            ...borderShadowStyle,
            ...borderRadiusStyle,
            ...style,
          }}
        >
          {children || <DefaultContent />}
        </div>
      )}
    </BackgroundColor>
  );
};

UniversalContainer.displayName = 'Universal Container';
UniversalContainer.defaultProps = universalContainerDefaultProps;
UniversalContainer.propTypes = universalContainerPropTypes;

const isTestEnv = process?.env?.NODE_ENV;
/**
 *
 * @param {*} componentDocument
 * @param {string} fieldLabel
 * @param {Array<'backgroundImage'|'backgroundColor'|'padding'|'margin'|'border'|'width'|'alignment'|'hide'>} allowedControls allowedControls: List of controls to be shown in the editor
 * @param {string} fieldName
 * @param {{disableGroup: boolean, customLabels: {background: string}}} options
 * @returns
 */
UniversalContainer.getEditingConfig = (
  component,
  fieldLabel = 'Universal Container',
  allowedControls = isTestEnv
    ? ['backgroundColor', 'backgroundImage', 'border', 'margin', 'padding']
    : [],
  fieldName = 'universalContainer',
  options = {
    disableGroup: false,
    customLabels: {
      background: 'Background',
    },
  }
) => {
  const componentDocument = set(
    component,
    `props.${fieldName}.alignment.direction`,
    'column'
  );
  const shouldDisplayCustomPadding =
    get(componentDocument, `props.${fieldName}.padding.type`, 'currency') ===
    'custom';
  const shouldDisplayCustomMargin =
    get(componentDocument, `props.${fieldName}.margin.type`, 'currency') ===
    'custom';

  const paddingType = get(
    componentDocument,
    `props.${fieldName}.padding.type`,
    'currency'
  );

  const marginType = get(
    componentDocument,
    `props.${fieldName}.margin.type`,
    'currency'
  );

  const paddingInputConfig = shouldDisplayCustomPadding
    ? buildSpacingInputs(
        numberInputSpec,
        `${fieldName}.padding.${paddingType}`,
        ['top', 'right', 'left', 'bottom'],
        '(pixels)'
      )
    : buildSpacingInputs(
        stringSelectSpec,
        `${fieldName}.padding.${paddingType}`,
        ['top', 'right', 'left', 'bottom'],
        null,
        !shouldDisplayCustomPadding
      );

  const paddingEditorControls = allowedControls.includes('padding')
    ? [
        group('Padding', [
          stringSelectSpec(
            `${fieldName}.padding.type`,
            'Padding Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...paddingInputConfig,
        ]),
      ]
    : [];

  const marginInputConfig = shouldDisplayCustomMargin
    ? buildSpacingInputs(
        numberInputSpec,
        `${fieldName}.margin.${marginType}`,
        ['top', 'right', 'left', 'bottom'],
        '(pixels)'
      )
    : buildSpacingInputs(
        stringSelectSpec,
        `${fieldName}.margin.${marginType}`,
        ['top', 'right', 'left', 'bottom'],
        null,
        !shouldDisplayCustomMargin
      );

  const marginEditorControls = allowedControls.includes('margin')
    ? [
        group('Margin', [
          stringSelectSpec(
            `${fieldName}.margin.type`,
            'Margin Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...marginInputConfig,
        ]),
      ]
    : [];

  const backgroundImageEditorControls = allowedControls.includes(
    'backgroundImage'
  )
    ? [
        stringInputSpec(
          `${fieldName}.backgroundImageDesktop`,
          'Background Image URL (Desktop)'
        ),
        stringInputSpec(
          `${fieldName}.backgroundImageTablet`,
          'Background Image URL (Tablet)'
        ),
        stringInputSpec(
          `${fieldName}.backgroundImageMobile`,
          'Background Image URL (Mobile)'
        ),
        stringSelectSpec(
          `${fieldName}.backgroundSize`,
          'Background Size',
          backgroundSizes
        ),
      ]
    : [];

  const backgroundColorEditorControls = allowedControls.includes(
    'backgroundColor'
  )
    ? [...BackgroundColor.getEditingConfig(componentDocument, fieldName)]
    : [];

  const backgroundConfigEditorControls =
    backgroundImageEditorControls.length || backgroundColorEditorControls.length
      ? [
          group(options?.customLabels?.background || 'Background', [
            ...backgroundImageEditorControls,
            ...backgroundColorEditorControls,
          ]),
        ]
      : [];

  const borderColorInputConfig =
    get(componentDocument, `props.${fieldName}.border.type`, 'none') === 'color'
      ? [
          stringInputSpec(
            `${fieldName}.border.borderColor`,
            'Border Color Hex (eg: "#fff" - default is #cdcdcd)'
          ),
          numberInputSpec(
            `${fieldName}.border.borderWidth`,
            'Border Width (eg: "2" - default is 0, change it to a higher value to be visible)'
          ),
        ]
      : [];

  const borderShadowInputConfig =
    get(componentDocument, `props.${fieldName}.border.type`, 'none') ===
    'shadow'
      ? [
          stringInputSpec(
            `${fieldName}.border.shadowColor`,
            'Drop Shadow Color Hex (eg: "#fff" - default is #cdcdcd)'
          ),
        ]
      : [];
  const currencyOrCustomRoundedBorderConfig =
    get(
      componentDocument,
      `props.${fieldName}.border.borderRounded.type`,
      'currency'
    ) === 'currency'
      ? [
          stringSelectSpec(
            `${fieldName}.border.borderRounded.currencyValue`,
            'Currency Value',
            [
              { label: 'None', value: '' },
              { label: 'border-radius-1', value: 'border-radius-1' },
              { label: 'border-radius-2', value: 'border-radius-2' },
            ],
            {
              help: 'Defaults to "None"',
            }
          ),
        ]
      : [
          numberInputSpec(
            `${fieldName}.border.borderRounded.value`,
            'Custom Value'
          ),
        ];

  const borderRoundedInputConfig =
    get(componentDocument, `props.${fieldName}.border.type`, 'none') ===
    'rounded'
      ? [
          stringSelectSpec(
            `${fieldName}.border.borderRounded.type`,
            'Border Rounded Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...currencyOrCustomRoundedBorderConfig,
        ]
      : [];

  const borderInputConfig = [
    group('Border', [
      stringSelectSpec(`${fieldName}.border.type`, 'Border', [
        { label: 'None', value: 'none' },
        { label: 'Rounded Corners', value: 'rounded' },
        { label: 'Border Color', value: 'color' },
        { label: 'Border Shadow', value: 'shadow' },
      ]),
      ...borderColorInputConfig,
      ...borderShadowInputConfig,
      ...borderRoundedInputConfig,
    ]),
  ];

  const borderEditorControls = allowedControls.includes('border')
    ? [...borderInputConfig]
    : [];

  const widthInputConfig =
    get(componentDocument, `props.${fieldName}.width.type`, 'currency') ===
    'currency'
      ? [
          stringSelectSpec(
            `${fieldName}.width.currency.value`,
            'Currency Value',
            [
              { label: 'None', value: '' },
              { label: 'Width one thirds', value: 'calc(100% / 3)' },
              { label: 'Width two thirds', value: 'calc(2 * 100% / 3)' },
              { label: 'Width 10', value: '10%' },
              { label: 'Width 20', value: '20%' },
              { label: 'Width 25', value: '25%' },
              { label: 'Width 30', value: '30%' },
              { label: 'Width 40', value: '40%' },
              { label: 'Width 50', value: '50%' },
              { label: 'Width 60', value: '60%' },
              { label: 'Width 70', value: '70%' },
              { label: 'Width 75', value: '75%' },
              { label: 'Width 80', value: '80%' },
              { label: 'Width 90', value: '90%' },
              { label: 'Width 100', value: '100%' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
        ]
      : [numberInputSpec(`${fieldName}.width.custom.value`, 'Custom Value')];

  const widthEditorControls = allowedControls.includes('width')
    ? [
        group('Width', [
          stringSelectSpec(
            `${fieldName}.width.type`,
            'Width Type',
            [
              { label: 'Currency', value: 'currency' },
              { label: 'Custom', value: 'custom' },
            ],
            {
              help: 'Defaults to "Currency"',
            }
          ),
          ...widthInputConfig,
          boolCheckboxSpec(
            `${fieldName}.width.screenSizes.lg`,
            'Apply Width In Desktop Screens'
          ),
          boolCheckboxSpec(
            `${fieldName}.width.screenSizes.md`,
            'Apply Width In Tablet Screens'
          ),
          boolCheckboxSpec(
            `${fieldName}.width.screenSizes.sm`,
            'Apply Width In Mobile Screens'
          ),
        ]),
      ]
    : [];

  const hideEditorControls = allowedControls.includes('hide')
    ? [
        group('Hide', [
          boolCheckboxSpec(`${fieldName}.hide.lg`, 'Hide In Desktop Screens'),
          boolCheckboxSpec(`${fieldName}.hide.md`, 'Hide In Tablet Screens'),
          boolCheckboxSpec(`${fieldName}.hide.sm`, 'Hide In Mobile Screens'),
        ]),
      ]
    : [];

  const orientation = get(
    componentDocument,
    `props.${fieldName}.alignment.direction`,
    ''
  );

  const alignmentInputConfig = [
    group('Alignment', [
      stringSelectSpec(
        `${fieldName}.alignment.horizontal`,
        orientation === 'column' ? 'Horizontal' : 'Vertical',
        [
          {
            label: orientation === 'column' ? 'Left' : 'Top',
            value: 'flex-start',
          },
          { label: 'Center', value: 'center' },
          {
            label: orientation === 'column' ? 'Right' : 'Bottom',
            value: 'flex-end',
          },
        ]
      ),
      stringSelectSpec(
        `${fieldName}.alignment.vertical`,
        orientation === 'column' ? 'Vertical' : 'Horizontal',
        [
          {
            label: orientation === 'column' ? 'Top' : 'Left',
            value: 'flex-start',
          },
          { label: 'Center', value: 'center' },
          {
            label: orientation === 'column' ? 'Bottom' : 'Right',
            value: 'flex-end',
          },
        ]
      ),
    ]),
  ];

  const alignmentEditorControls = allowedControls.includes('alignment')
    ? [...alignmentInputConfig]
    : [];

  const editorPanelOptions = [
    ...widthEditorControls,
    ...alignmentEditorControls,
    ...backgroundConfigEditorControls,
    ...paddingEditorControls,
    ...marginEditorControls,
    ...borderEditorControls,
    ...hideEditorControls,
  ];

  return [
    options.disableGroup
      ? wrapper(editorPanelOptions)
      : group(fieldLabel, editorPanelOptions),
  ];
};

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(UniversalContainer);
